import React, { useState } from 'react';
import { SettingsPage } from '../../../components/SettingsPage/SettingsPage';
import { SettingCard } from '../../../components/SettingCards/SettingCard/SettingCard';
import { Dropdown } from '../../../components/Dropdown/Dropdown';
import { SecondaryButton, PrimaryButton } from '../../../components/Buttons/Buttons';
import { ButtonGroup } from '../../../components/Buttons/ButtonGroup/ButtonGroup';
import { SEND_FEEDBACK_SETTING_IDS } from '../../../config/SettingIds';
import { SettingsNativeBridgeFactory } from '../../../bridge/SettingsNativeBridgeFactory';
import { LogCollectionDialog } from '../../../customSettings/LogCollectionSetting/LogCollectionDialog';
import { LogFileCard } from '../../../customSettings/LogCollectionSetting/LogFileCard';
import { useLogCollection } from '../../../customSettings/LogCollectionSetting/useLogCollection';
import {
	SEND_FEEDBACK_PAGE_STRINGS,
	LOG_COLLECTION_STRINGS,
} from '../SupportAndTroubleshootStrings';
import { StatusPassIcon } from '../../../Icons/StatusIcons/StatusIcons';
import { TrashIcon } from '../../../Icons/TrashIcon/TrashIcon';
import { WarningIcon } from '../../../Icons/WarningIcon/WarningIcon';
import { FileIcon } from '../../../Icons/FileIcon/FileIcon';
import { CopyIcon } from '../../../Icons/CopyIcon/CopyIcon';
import { TextInput } from '../../../components/TextInput/TextInput';
import { IconButton } from '../../../components/Buttons/IconButton/IconButton';
import './SendFeedbackPage.css';
import { SettingCardContent } from '../../../components/SettingCards/SettingCard/SettingCardContent/SettingCardContent';

export interface AttachmentInfo {
	fileName: string;
	fileSize: string;
}

export interface ReportCreatedInfo {
	reportPath: string;
	emailAddress: string;
}

const LOG_LEVEL_OPTIONS = [
	{ value: 'low', label: SEND_FEEDBACK_PAGE_STRINGS.LOG_LEVEL_LOW },
	{ value: 'medium', label: SEND_FEEDBACK_PAGE_STRINGS.LOG_LEVEL_MEDIUM },
	{ value: 'verbose', label: SEND_FEEDBACK_PAGE_STRINGS.LOG_LEVEL_VERBOSE },
];

const MAX_ATTACHMENT_SIZE_MB = 25;

const parseSizeMB = (fileSize: string): number => {
	const match = fileSize.match(/^([\d.]+)\s*MB?$/i);
	return match ? parseFloat(match[1]) : 0;
};

const isOversized = (file: AttachmentInfo) =>
	parseSizeMB(file.fileSize) > MAX_ATTACHMENT_SIZE_MB;

interface SendFeedbackPageProps {
	title: string;
	description?: string;
}

export const SendFeedbackPage: React.FC<SendFeedbackPageProps> = ({ title }) => {
	const [feedbackTitle, setFeedbackTitle] = useState('');
	const [feedbackDescription, setFeedbackDescription] = useState('');
	const [logLevel, setLogLevel] = useState('low');
	const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);
	const [reportInfo, setReportInfo] = useState<ReportCreatedInfo | null>(null);
	const {
		dialogMode,
		logFile,
		handleStartCollecting,
		handleCollectExisting,
		handleStop,
		handleCancel,
		handleDownload,
		handleDelete,
	} = useLogCollection();

	const isSendDisabled = feedbackTitle.trim() === '' || feedbackDescription.trim() === '';

	const parseAttachmentsFromResponse = (
		settingValue: string | undefined
	): AttachmentInfo[] => {
		if (!settingValue) return [];
		try {
			const parsed = JSON.parse(settingValue);
			if (Array.isArray(parsed)) return parsed as AttachmentInfo[];
		} catch {
			// not an attachment list
		}
		return [];
	};

	const handleChooseAttachment = async () => {
		const bridge = SettingsNativeBridgeFactory.getBridge();
		const response = await bridge.setSetting({
			settingKey: SEND_FEEDBACK_SETTING_IDS.CHOOSE_ATTACHMENT,
			settingValue: true,
		});
		setAttachments(parseAttachmentsFromResponse(response?.data?.settingValue));
	};

	const handleDeleteAttachment = (fileName: string) => {
		setAttachments((prev) => prev.filter((a) => a.fileName !== fileName));
	};

	const handleCreateReport = async () => {
		const bridge = SettingsNativeBridgeFactory.getBridge();
		const feedbackData = {
			title: feedbackTitle,
			description: feedbackDescription,
			logFile: logFile ? logFile.fileName : null,
			attachments: attachments.map((a) => a.fileName),
		};
		const response = await bridge.setSetting({
			settingKey: SEND_FEEDBACK_SETTING_IDS.CREATE_REPORT,
			settingValue: JSON.stringify(feedbackData),
		});
		let reportPath = '';
		let emailAddress = '';
		if (response?.data?.settingValue) {
			try {
				const parsed = JSON.parse(response.data.settingValue);
				reportPath = parsed.reportPath || '';
				emailAddress = parsed.emailAddress || '';
			} catch {
				// invalid response
			}
		}
		setReportInfo({ reportPath, emailAddress });
	};

	const handleCreateAnotherReport = () => {
		setFeedbackTitle('');
		setFeedbackDescription('');
		setLogLevel('low');
		setAttachments([]);
		setReportInfo(null);
	};

	const handleCopyEmail = () => {
		if (reportInfo?.emailAddress) {
			navigator.clipboard.writeText(reportInfo.emailAddress);
		}
	};

	return (
		<>
			<SettingsPage
				title={title}
				settings={
					reportInfo ? (
						<>
							<SettingCard
								settingId="send-feedback-report-created"
								title=""
								content={
									<SettingCardContent className="send-feedback-report-created">
										<div className="send-feedback-report-created-header">
											<StatusPassIcon />
											<span className="send-feedback-report-created-title">
												{SEND_FEEDBACK_PAGE_STRINGS.REPORT_CREATED}
											</span>
										</div>
										<p className="send-feedback-report-created-path">
											{SEND_FEEDBACK_PAGE_STRINGS.REPORT_SAVED_TO}{' '}
											<a
												href={reportInfo.reportPath}
												className="send-feedback-report-link"
												title={reportInfo.reportPath}
											>
												{reportInfo.reportPath}
											</a>
										</p>
										<p className="send-feedback-report-email-label">
											{SEND_FEEDBACK_PAGE_STRINGS.SEND_REPORT_EMAIL_LABEL}
										</p>
										<div className="send-feedback-report-email-row">
											<a
												href={`mailto:${reportInfo.emailAddress}`}
												className="send-feedback-report-email-link"
											>
												{reportInfo.emailAddress}
											</a>
											<IconButton
												onClick={handleCopyEmail}
												ariaLabel={SEND_FEEDBACK_PAGE_STRINGS.COPY_EMAIL_ADDRESS}
												showBorder={false}
											>
												<CopyIcon />
											</IconButton>
										</div>
									</SettingCardContent>
								}
							/>
							<div className="send-feedback-footer">
								<SecondaryButton
									onClick={handleCreateAnotherReport}
									ariaLabel={SEND_FEEDBACK_PAGE_STRINGS.CREATE_ANOTHER_REPORT}
								>
									{SEND_FEEDBACK_PAGE_STRINGS.CREATE_ANOTHER_REPORT}
								</SecondaryButton>
							</div>
						</>
					) : (
						<>
							<p className="send-feedback-page-description">
								{SEND_FEEDBACK_PAGE_STRINGS.PAGE_DESCRIPTION}
							</p>
							<SettingCard
								settingId="send-feedback-fields"
								title=""
								content={
									<SettingCardContent>
										<TextInput
											label={SEND_FEEDBACK_PAGE_STRINGS.TITLE_LABEL}
											value={feedbackTitle}
											onChange={setFeedbackTitle}
											required
											id="send-feedback-title"
											ariaLabel={SEND_FEEDBACK_PAGE_STRINGS.FEEDBACK_TITLE}
											className="send-feedback-field-group"
										/>
										<TextInput
											label={SEND_FEEDBACK_PAGE_STRINGS.DESCRIPTION_LABEL}
											value={feedbackDescription}
											onChange={setFeedbackDescription}
											placeholder={SEND_FEEDBACK_PAGE_STRINGS.DESCRIPTION_PLACEHOLDER}
											required
											multiline
											rows={5}
											id="send-feedback-description"
											ariaLabel={SEND_FEEDBACK_PAGE_STRINGS.FEEDBACK_DESCRIPTION}
											className="send-feedback-field-group"
										/>
									</SettingCardContent>
								}
							/>
							<SettingCard
								settingId="send-feedback-logs"
								title={SEND_FEEDBACK_PAGE_STRINGS.LOGS_CARD_TITLE}
								content={
									<SettingCardContent className="send-feedback-logs-content">
										<p className="send-feedback-logs-description">
											Select the log level and then choose to either{' '}
											<strong>{LOG_COLLECTION_STRINGS.START_COLLECTING_LOGS}</strong> to
											reproduce the issue, or select{' '}
											<strong>{LOG_COLLECTION_STRINGS.COLLECT_EXISTING_LOGS}</strong> if
											you can&apos;t reproduce the issue.
										</p>

										<Dropdown
											className="send-feedback-log-level"
											options={LOG_LEVEL_OPTIONS}
											value={logLevel}
											onChange={setLogLevel}
											label={SEND_FEEDBACK_PAGE_STRINGS.LOG_LEVEL_LABEL}
										/>

										<ButtonGroup>
											<SecondaryButton
												onClick={() => handleStartCollecting(logLevel)}
												ariaLabel={LOG_COLLECTION_STRINGS.START_COLLECTING_LOGS}
											>
												{LOG_COLLECTION_STRINGS.START_COLLECTING_LOGS}
											</SecondaryButton>
											<SecondaryButton
												onClick={handleCollectExisting}
												ariaLabel={LOG_COLLECTION_STRINGS.COLLECT_EXISTING_LOGS}
											>
												{LOG_COLLECTION_STRINGS.COLLECT_EXISTING_LOGS}
											</SecondaryButton>
										</ButtonGroup>
										{logFile && (
											<LogFileCard
												logFile={logFile}
												allowDownloadFile={false}
												onDownload={handleDownload}
												onDelete={handleDelete}
											/>
										)}
									</SettingCardContent>
								}
							/>
							<SettingCard
								settingId="send-feedback-attachments"
								title={SEND_FEEDBACK_PAGE_STRINGS.ATTACHMENTS_CARD_TITLE}
								description={SEND_FEEDBACK_PAGE_STRINGS.ATTACHMENTS_CARD_DESCRIPTION}
								content={
									<SettingCardContent className="send-feedback-attachments">
										<div className="send-feedback-attachments-row">
											<SecondaryButton
												onClick={handleChooseAttachment}
												ariaLabel={SEND_FEEDBACK_PAGE_STRINGS.CHOOSE_FILE}
											>
												{SEND_FEEDBACK_PAGE_STRINGS.CHOOSE_FILE}
											</SecondaryButton>
											<span className="send-feedback-file-size-note">
												{SEND_FEEDBACK_PAGE_STRINGS.FILE_SIZE_NOTE}
											</span>
										</div>
										{attachments.length > 0 && (
											<div className="send-feedback-attachment-tiles-wrapper">
												<span className="send-feedback-attachment-count">
													{attachments.length}{' '}
													{attachments.length === 1
														? SEND_FEEDBACK_PAGE_STRINGS.FILE_ADDED
														: SEND_FEEDBACK_PAGE_STRINGS.FILES_ADDED}{' '}
													{SEND_FEEDBACK_PAGE_STRINGS.ADDED}
												</span>
												<div className="send-feedback-attachment-tiles">
													{attachments.map((file) => {
														const oversized = isOversized(file);
														return (
															<div
																key={file.fileName}
																className={`send-feedback-attachment-tile${oversized ? ' oversized' : ''}`}
															>
																<div className="send-feedback-attachment-thumbnail">
																	{oversized ? <WarningIcon /> : <FileIcon />}
																	<IconButton
																		onClick={() => handleDeleteAttachment(file.fileName)}
																		ariaLabel={`${SEND_FEEDBACK_PAGE_STRINGS.DELETE} ${file.fileName}`}
																		tooltip={SEND_FEEDBACK_PAGE_STRINGS.DELETE}
																		variant="delete"
																		showBorder={false}
																		className="send-feedback-attachment-delete-btn"
																	>
																		<TrashIcon />
																	</IconButton>
																</div>
																<span
																	className="send-feedback-attachment-tile-name"
																	title={file.fileName}
																>
																	{file.fileName}
																</span>
																<span
																	className={`send-feedback-attachment-tile-size${oversized ? ' oversized' : ''}`}
																>
																	{file.fileSize}
																</span>
															</div>
														);
													})}
												</div>
											</div>
										)}
									</SettingCardContent>
								}
							/>
							<div className="send-feedback-footer">
								<PrimaryButton
									onClick={handleCreateReport}
									disabled={isSendDisabled}
									ariaLabel={SEND_FEEDBACK_PAGE_STRINGS.CREATE_REPORT}
								>
									{SEND_FEEDBACK_PAGE_STRINGS.CREATE_REPORT}
								</PrimaryButton>
							</div>
						</>
					)
				}
			/>
			<LogCollectionDialog
				isOpen={dialogMode !== null}
				mode={dialogMode}
				onStop={handleStop}
				onCancel={handleCancel}
			/>
		</>
	);
};
